import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Offcanvas, Table, Button, Spinner, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import DnsForm from './DnsForm';
import withPermission from '../../utils/withPermission';

const DnsList = ({
    show,
    handleClose,
    domainId,
    domainName,
    zoneId,
    instanceIp,
    isTabMode = false, 
    onFormToggle // New prop
}) => {
    const [dnsRecords, setDnsRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const [selectedDnsRecordId, setSelectedDnsRecordId] = useState(null);
    const [selectedDnsRecordData, setSelectedDnsRecordData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchDnsRecords = async () => {
        if (!domainId) return;
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/dns-records`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    domain_id: domainId
                },
                skipLoader: true, // Custom flag to indicate skipping global loader
            });

            console.log("Cloudflare API Response:", response.data.CloudflareData);

            let records = response.data.CloudflareData?.result || response.data.Data || [];
            
            if (searchTerm) {
                const lowerSearch = searchTerm.toLowerCase();
                records = records.filter(r => 
                    (r.name && r.name.toLowerCase().includes(lowerSearch)) || 
                    (r.content && r.content.toLowerCase().includes(lowerSearch)) ||
                    (r.type && r.type.toLowerCase().includes(lowerSearch))
                );
            }

            setDnsRecords(records);
        } catch (error) {
            console.error('Error fetching DNS records:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((show || isTabMode) && domainId) {
            const timeout = setTimeout(() => {
                fetchDnsRecords();
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [show, domainId, isTabMode, searchTerm]);

    const handleShowForm = (record = null) => {
        if (record) {
            setSelectedDnsRecordId(record.id);
            setSelectedDnsRecordData(record);
        } else {
            setSelectedDnsRecordId(null);
            setSelectedDnsRecordData(null);
        }
        setShowForm(true);
        if (onFormToggle) onFormToggle(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        if (onFormToggle) onFormToggle(false);
        setSelectedDnsRecordId(null);
        setSelectedDnsRecordData(null);
    };

    const addDnsRecord = async (newRecord) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            await axios.post(`${backendUrl}/api/dns-records`, newRecord, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            fetchDnsRecords();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'DNS Record Added!',
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (error) {
            if (error.response?.data?.errors) return error.response.data.errors;
            console.error('Error adding DNS record:', error);
        }
    };

    const editDnsRecord = async (id, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            await axios.put(`${backendUrl}/api/dns-records/${id}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            fetchDnsRecords();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'DNS Record Updated!',
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (error) {
            if (error.response?.data?.errors) return error.response.data.errors;
            console.error('Error updating DNS record:', error);
        }
    };

    const deleteDnsRecord = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This DNS record will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                await axios.delete(`${backendUrl}/api/dns-records/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    data: { domain_id: domainId } // pass domain_id so backend can delete from CF even if not in local DB
                });
                fetchDnsRecords();
                Swal.fire("Deleted!", "Record has been deleted.", "success");
            } catch (error) {
                console.error('Error deleting DNS record:', error);
                Swal.fire("Error!", "Failed to delete record.", "error");
            }
        }
    };

    const WithEditPermission = withPermission(({ children }) => children, ['domain-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['domain-delete']);
    const WithCreatePermission = withPermission(({ children }) => children, ['domain-create']);

    const indexOfLastRecord = currentPage * itemsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
    const currentRecords = dnsRecords.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(dnsRecords.length / itemsPerPage);

    const renderContent = () => (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3 px-4">
                <div className="d-flex align-items-center">
                    <h6 className="mb-0 fw-semibold text-primary me-2">DNS Records ({dnsRecords.length})</h6>
                    <i 
                        className="mdi mdi-refresh font-size-20 text-primary ms-2" 
                        style={{ cursor: 'pointer', transition: 'transform 0.4s' }}
                        onClick={(e) => {
                            e.currentTarget.classList.add('mdi-spin');
                            fetchDnsRecords();
                            setTimeout(() => {
                                e.target.classList.remove('mdi-spin');
                            }, 1000);
                        }}
                        title="Refresh Records"
                    ></i>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div className="position-relative">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search records..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{ paddingLeft: '30px', width: '200px' }}
                        />
                        <i className="bx bx-search position-absolute text-muted" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }}></i>
                    </div>
                    <WithCreatePermission>
                        <button
                            type="button"
                            className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                            onClick={() => handleShowForm()}
                            title="Add DNS Record"
                        >
                            <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                        </button>
                    </WithCreatePermission>
                </div>
            </div><br/><br/>

            <div className="contacts-table-modern px-4">
                <div className="table-responsive" style={{ overflow: 'visible', minHeight: '200px', paddingBottom: '100px' }}>
                    <table className="table table-modern">
                        <thead>
                            <tr>
                                 <th scope="col" style={{ width: '60px' }}>#</th>
                                <th scope="col">Type</th>
                                <th scope="col">Name</th>
                                <th scope="col">Value/Content</th>
                                <th scope="col">TTL</th>
                                <th scope="col">Proxied</th>
                                <th scope="col" className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                    </td>
                                </tr>
                            ) : dnsRecords.length > 0 ? (
                                 currentRecords.map((record, index) => (
                                    <tr key={record.id}>
                                        <td className="text-muted">{indexOfFirstRecord + index + 1}</td>
                                        <td>
                                            <span className="badge badge-pill badge-soft-primary font-size-12 text-primary">
                                                {record.type}
                                            </span>
                                        </td>
                                        <td className="fw-bold text-dark">{record.name}</td>
                                        <td className="text-muted">
                                            <div className="text-truncate" style={{ maxWidth: '250px' }} title={record.content}>
                                                {record.content}
                                            </div>
                                        </td>
                                        <td><span className="text-muted">{record.ttl}</span></td>
                                        <td>
                                            {record.proxied ? (
                                                <span className="badge badge-pill badge-soft-warning font-size-12 text-warning">
                                                    <i className="bx bxs-cloud me-1"></i> True
                                                </span>
                                            ) : (
                                                <span className="badge badge-pill badge-soft-secondary font-size-12 text-secondary">
                                                    <i className="bx bx-cloud-light me-1"></i> False
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <Dropdown drop="start" align="end">
                                                <Dropdown.Toggle as="div" className="text-muted" style={{ cursor: 'pointer' }}>
                                                    <i className="mdi mdi-dots-horizontal font-size-18"></i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="dropdown-menu-end font-size-14">
                                                    <WithEditPermission>
                                                        <Dropdown.Item onClick={() => handleShowForm(record)}>Edit</Dropdown.Item>
                                                    </WithEditPermission>
                                                    <WithDeletePermission>
                                                        <Dropdown.Item className="text-danger" onClick={() => deleteDnsRecord(record.id)}>Delete</Dropdown.Item>
                                                    </WithDeletePermission>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted">
                                        No DNS records found for this domain.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-end mt-3 mb-4">
                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                        <i className="mdi mdi-chevron-left"></i>
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                        <i className="mdi mdi-chevron-right"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>

            <DnsForm
                showModal={showForm}
                handleClose={handleCloseForm}
                domainId={domainId}
                domainName={domainName}
                zoneId={zoneId}
                instanceIp={instanceIp}
                dnsRecordId={selectedDnsRecordId}
                dnsRecordData={selectedDnsRecordData}
                addDnsRecord={addDnsRecord}
                editDnsRecord={editDnsRecord}
            />
        </>
    );

    if (isTabMode) {
        return renderContent();
    }

    return (
        <Offcanvas 
            show={show} 
            onHide={handleClose} 
            placement="end" 
            style={{ width: '1150px' }}
            backdrop="static"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    <div className="d-flex align-items-center">
                        <i className="bx bx-globe me-2 text-primary"></i>
                        <div>
                            <h5 className="mb-0">DNS Management</h5>
                            <small className="text-muted">{domainName} {zoneId && `(${zoneId})`}</small>
                        </div>
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
                {renderContent()}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default DnsList;
